import React, {useCallback, useEffect, useState} from "react";
import {
  Col,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import {FaAngleRight} from "react-icons/fa";
import {useDispatch, useSelector} from "react-redux";
import {useCleanupOnUnload} from "../../hooks/useCleanupOnUnload";
import {
  createSensorDowntimeAlert,
  requestDowntimeCreateData,
  requestSensorDowntimeGraph,
  requestSensorList,
  storeSensorsForAlert,
} from "../../redux/Actions/alerts.actions";
import {storeDataExplorerAnalysis} from "../../redux/Actions/data-explorer.actions";

import {
  getDowntimeCreateData,
  getSensorForAlert,
} from "../../redux/Selectors/alerts.selector";
import {getDataExplorerAnalysis} from "../../redux/Selectors/data-explorer.selectors";
import {useScopeAndAppliances} from "../../hooks/useScopeAndAppliances";
import {useFetchSensorAlerts} from "../../views/SensorAlerts/sensorAlertsTable/useSensorAlertTableData";
import AsteriskIcon from "../asteriskIcon";
import ZeusButton from "../Button";
import Cells from "../Charts/Containers/Cell/Cells";
import Loader from "../Loader";
import AlertFormLabel from "./components/AlertFormLabel";
import SelectCreator from "./components/SelectCreator";
import TreeSelector from "./components/TreeSelector";
import ToggleCreator from "./components/ToggleCreator";
import "./forms.scss";

const AddSensorDowntime = (props) => {
  const {closeAlertForm, data, showAlertForm} = props;
  const dispatch = useDispatch();
  const {refetch} = useFetchSensorAlerts();
  const downtimeCreateOptions = useSelector(getDowntimeCreateData);
  const sensorsList = useSelector(getSensorForAlert);
  const analysis = useSelector(getDataExplorerAnalysis);
  const {alarm_levels = [], time_unit_list = []} = downtimeCreateOptions;
  const [formData, setFormData] = useState(data || {});
  const {
    scopeOptions: siteScopeOptions,
    applianceOptions: controllerList,
    fetchScope,
    fetchAppliances,
  } = useScopeAndAppliances();
  const {
    name,
    description,
    level,
    inventory,
    sensor,
    every_type,
    every_value,
    re_type,
    re_value,
    trigger_type,
    trigger_value,
    enabled = true,
    scope = [],
    sms_enabled = true,
  } = formData;

  const cleanup = useCallback(() => {
    dispatch(storeSensorsForAlert([]));
    dispatch(storeDataExplorerAnalysis({}));
  }, [dispatch]);

  useCleanupOnUnload(cleanup);

  const requestGraphData = (value) => {
    if (value) {
      dispatch(requestSensorDowntimeGraph({mac: inventory, sensor: value}));
    } else {
      dispatch(storeDataExplorerAnalysis({}));
    }
  };

  useEffect(() => {
    fetchScope();
    dispatch(requestDowntimeCreateData());
    fetchAppliances(scope);

    if (inventory) {
      dispatch(requestSensorList({mac: inventory}));
    }

    requestGraphData(sensor);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = (e, type, name) => {
    let update_modal_data = {...formData};
    //select
    if (type === 1) {
      update_modal_data = Object.assign({}, update_modal_data, {
        [name]: e?.target?.value,
      });
    }
    //number or text
    if (type === 2) {
      update_modal_data = Object.assign({}, update_modal_data, {
        [name]: e?.target?.value,
      });
    }
    // toggle
    if (type === 3) {
      update_modal_data = Object.assign({}, update_modal_data, {
        [name]: e,
      });
    }

    //removing re_value if re_type is cleared
    if (name === "re_type") {
      if (!e.target.value) {
        // eslint-disable-next-line no-unused-vars
        const {re_type, ...rest} = update_modal_data;
        update_modal_data = rest;
        update_modal_data = Object.assign({}, update_modal_data, {
          re_value: "",
        });
      }
    }
    if (name === "inventory") {
      update_modal_data = Object.assign({}, update_modal_data, {
        sensor: null,
      });
      dispatch(requestSensorList({mac: update_modal_data.inventory}));
      requestGraphData(null);
    }

    if (name === "sensor") {
      requestGraphData(e?.target?.value);
    }
    setFormData(update_modal_data);
  };

  const handleScopeChange = (value) => {
    const callback = (newOptions) => {
      const isSelectedInventoryAvailable = newOptions.some(
        (item) => item.value === inventory
      );

      if (!isSelectedInventoryAvailable) {
        setFormData((prevData) => ({
          ...prevData,
          inventory: null,
          sensor: null,
        }));

        requestGraphData(null);
      }
    };

    setFormData((prevData) => ({
      ...prevData,
      scope: value,
    }));

    fetchAppliances(value, callback);
  };

  const handleSubmit = () => {
    let downtimeCreateData = {
      name,
      description,
      level,
      mac: inventory,
      every_type,
      every_value,
      re_type,
      re_value,
      trigger_type,
      trigger_value,
      enabled,
      sensor,
      scope,
      sms_enabled,
    };

    if (!re_value) {
      // eslint-disable-next-line no-unused-vars
      const {re_value, re_type, ...rest} = downtimeCreateData;
      downtimeCreateData = rest;
    }
    if (!re_type) {
      // eslint-disable-next-line no-unused-vars
      const {re_type, re_value, ...rest} = downtimeCreateData;
      downtimeCreateData = rest;
    }

    const callback = () => {
      closeAlertForm();
      refetch();
    };

    dispatch(createSensorDowntimeAlert(downtimeCreateData, formData.id, callback));
  };

  return (
    <Modal
      unmountOnClose={true}
      isOpen={showAlertForm}
      backdrop={"static"}
      className="custom-container"
      contentClassName="custom-width"
    >
      <Loader />
      <ModalHeader className="alert_modal_header" toggle={closeAlertForm}>
        <Col xs="12">
          <div style={{display: "flex", alignItems: "start"}}>
            <AsteriskIcon />
            <Input
              type="text"
              name="name"
              placeholder={"Enter Alert Name"}
              className="modal_edit_header br-none roboto-bold"
              value={name}
              onChange={(e) => handleOnChange(e, 2, "name")}
              data-cy="alert_Name"
            />
          </div>
        </Col>
        <div className="desc--type--container" style={{color: "#525f7f"}}>
          <Col sm="12" xl="5">
            <div style={{display: "flex", alignItems: "start"}}>
              <AsteriskIcon />
              <Input
                type="text"
                name="description"
                placeholder={"Enter Alert Description"}
                className="br-none modal_edit_desc"
                value={description}
                onChange={(e) => handleOnChange(e, 2, "description")}
                data-cy="alert_Desp"
              />
            </div>
          </Col>
          <Col sm="12" md="6" lg="4" xl="3">
            <div style={{display: "flex"}}>
              <div className="mt--3">
                <AsteriskIcon />
              </div>
              <SelectCreator
                closeMenuOnSelect={true}
                options={alarm_levels}
                onChange={(e) => {
                  handleOnChange(e, 1, "level");
                }}
                value={level}
                className={"alert-select-creator"}
                colSize={12}
                placeholder={"Select Type"}
                id={"alarmLevels"}
                classNamePrefix={"alarmLevels"}
              />
            </div>
          </Col>
        </div>
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* Form section */}
          <Col sm="12" xl="5">
            <div className="main-text-container">
              <FaAngleRight /> <span className="ml-1 main-text roboto-bold">DEFINE</span>
            </div>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Site(s)" />
                  </Col>
                  <Col md="9">
                    <TreeSelector
                      defaultValue={scope}
                      options={siteScopeOptions}
                      onChange={handleScopeChange}
                      childrenKey={"locations"}
                      id={"scope"}
                      classNamePrefix={"scope"}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text roboto-normal">Select one or more</p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Appliance" />
                  </Col>
                  <SelectCreator
                    closeMenuOnSelect={true}
                    options={controllerList}
                    onChange={(e) => {
                      handleOnChange(e, 1, "inventory");
                    }}
                    value={inventory}
                    className={"alert-select-creator"}
                    colSize={9}
                    placeholder={"Select Appliance"}
                    id={"controllerList"}
                    classNamePrefix={"controllerList"}
                  />
                </Row>
              </FormGroup>
            </Col>

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Sensor" />
                  </Col>
                  <SelectCreator
                    closeMenuOnSelect={true}
                    options={controllerList.length > 0 ? sensorsList : []}
                    onChange={(e) => {
                      handleOnChange(e, 1, "sensor");
                    }}
                    value={sensor}
                    className={"alert-select-creator"}
                    colSize={9}
                    placeholder={"Select Sensor"}
                    id={"sensorsList"}
                    classNamePrefix={"sensorsList"}
                  />
                </Row>
              </FormGroup>
            </Col>

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Trigger" />
                  </Col>
                  <Col md="9">
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={trigger_value}
                          name="trigger_value"
                          onChange={(e) => handleOnChange(e, 2, "trigger_value")}
                          placeholder="Enter value"
                          className="input-number-height"
                          data-cy="triggerValue"
                        />
                      </Col>

                      <SelectCreator
                        closeMenuOnSelect={true}
                        options={time_unit_list}
                        onChange={(e) => {
                          handleOnChange(e, 1, "trigger_type");
                        }}
                        value={trigger_type}
                        className={"alert-select-creator padding-top-mobile"}
                        colSize={6}
                        placeholder={"Select"}
                        id={"triggerUnit"}
                        classNamePrefix={"triggerUnit"}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text roboto-normal">
                      If inventory is down for last
                    </p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>

            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3" className="check--every--label">
                    <AlertFormLabel label="Check Every" />
                  </Col>
                  <Col md="9">
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={every_value}
                          name="every_value"
                          onChange={(e) => handleOnChange(e, 2, "every_value")}
                          placeholder="Enter value"
                          className="input-number-height"
                          data-cy="CheckEveryValue"
                        />
                      </Col>

                      <SelectCreator
                        closeMenuOnSelect={true}
                        options={time_unit_list}
                        onChange={(e) => {
                          handleOnChange(e, 1, "every_type");
                        }}
                        value={every_type}
                        className={"alert-select-creator padding-top-mobile"}
                        colSize={6}
                        placeholder={"Select"}
                        id={"CheckEveryUnit"}
                        classNamePrefix={"CheckEveryUnit"}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text check-every-text roboto-normal">
                      Frequency to check for alarm status
                    </p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>

            {/* Notify Section*/}
            <div className="main-text-container">
              <FaAngleRight />
              <span className="ml-1 main-text roboto-bold">RE-NOTIFY</span>
            </div>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="Alerts" required={false} />
                  </Col>
                  <Col md="9">
                    <Row>
                      <Col md="6">
                        <Input
                          type="number"
                          value={re_value}
                          name="re_value"
                          onChange={(e) => handleOnChange(e, 2, "re_value")}
                          placeholder="Enter value"
                          className="input-number-height"
                        />
                      </Col>

                      <SelectCreator
                        closeMenuOnSelect={true}
                        options={time_unit_list}
                        onChange={(e) => {
                          handleOnChange(e, 1, "re_type");
                        }}
                        value={re_type}
                        className={"alert-select-creator padding-top-mobile"}
                        colSize={6}
                        placeholder={"Select"}
                        isClearable={true}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col md={{size: 9, offset: 3}}>
                    <p className="alert-info-text roboto-normal">
                      If alert event is unresolved
                    </p>
                  </Col>
                </Row>
              </FormGroup>
            </Col>
            <Col sm="12" md="12" xs="12" lg="12">
              <FormGroup className="custom-margin-bottom">
                <Row>
                  <Col md="3">
                    <AlertFormLabel label="SMS Notifications" required={false} />
                  </Col>
                  <Col md="9">
                    <ToggleCreator
                      large={true}
                      testId="trigger"
                      onChange={() => {
                        handleOnChange(!sms_enabled, 3, "sms_enabled");
                      }}
                      checked={sms_enabled}
                    />
                  </Col>
                </Row>
              </FormGroup>
            </Col>
          </Col>
          {/* Graph section */}
          <Col sm="12" xl="7">
            <div className="main-text-container">
              <FaAngleRight />
              <span className="ml-1 main-text roboto-bold" data-cy={"preview"}>
                PREVIEW
              </span>
            </div>

            {analysis && (
              <div>
                <Cells
                  dashboardId={"downtime"}
                  cells={[analysis]}
                  isDraggable={false}
                  isResizable={false}
                />
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <ZeusButton
          type="default"
          withShadow
          withRadius
          onClick={handleSubmit}
          data-cy="submit-btn"
        >
          {formData.id ? "UPDATE" : "CREATE"}
        </ZeusButton>
        <ZeusButton
          type="info-small"
          withShadow
          withRadius
          onClick={closeAlertForm}
          data-cy="cancel-btn"
        >
          CANCEL
        </ZeusButton>
      </ModalFooter>
    </Modal>
  );
};

AddSensorDowntime.propTypes = {};

export default AddSensorDowntime;
